import { useRef, useState, useCallback } from 'react';
import combineBatches from '../helpers/combine-batches';
import getObjectDifference from '../helpers/get-object-difference';

const useSettingsState = (initialData = null) => {
  const initialState = useRef(initialData?.initialState || {});
  const [state, setState] = useState(initialData?.state || {});
  const chunkAttributes = useRef(initialData?.chunkAttributes || new Map());
  const modifiedAttributes = useRef(initialData?.modifiedAttributes || new Map());
  const lastModifiedAttributeOwners = useRef(initialData?.lastModifiedAttributeOwners || new Map());

  const recordChanges = useCallback((batch, { chunkId = null } = {}) => {
    if (!chunkId) {
      return;
    }

    Object.entries(batch).forEach(([, attributes]) => {
      Object.keys(attributes).forEach((attribute) => {
        lastModifiedAttributeOwners.current.set(attribute, chunkId);

        const existingAttrs = modifiedAttributes.current.get(chunkId) || new Set();
        existingAttrs.add(attribute);
        modifiedAttributes.current.set(chunkId, existingAttrs);
      });
    });
  }, []);

  const getModifiedAttributes = useCallback(
    (chunkId) => Array.from(modifiedAttributes.current.get(chunkId) || []),
    []
  );

  const getLastModifiedAttributes = useCallback(
    (chunkId) =>
      getModifiedAttributes(chunkId).filter(
        (attribute) => lastModifiedAttributeOwners.current.get(attribute) === chunkId
      ),
    [getModifiedAttributes]
  );

  const updateState = useCallback(
    (batch, { chunkId = null } = {}) => {
      if (!batch) {
        return;
      }

      recordChanges(batch, { chunkId });

      setState((currentState) => {
        const newState = combineBatches([currentState, batch]);

        if (chunkId) {
          chunkAttributes.current.set(
            chunkId,
            new Set([
              ...(chunkAttributes.current.get(chunkId) || new Set()),
              ...Object.values(batch).flatMap(Object.keys),
            ])
          );
        }

        return newState;
      });
    },
    [recordChanges]
  );

  const registerInitialState = useCallback((chunkId, chunkState) => {
    initialState.current = combineBatches([initialState.current, chunkState]);
    setState((currentState) => combineBatches([chunkState, currentState]));
  }, []);

  const getInitialState = useCallback(() => initialState.current, []);

  const getStateDiff = useCallback(() => getObjectDifference(initialState.current, state), [state]);

  const clearStateByChunkId = useCallback((chunkId) => {
    const attributesToClear = chunkAttributes.current.get(chunkId);

    if (!attributesToClear?.size) return;

    setState((currentState) => {
      const newState = { ...currentState };

      Object.keys(newState).forEach((elementId) => {
        attributesToClear.forEach((attrName) => delete newState[elementId][attrName]);

        if (!Object.keys(newState[elementId]).length) delete newState[elementId];
      });

      return newState;
    });

    chunkAttributes.current.delete(chunkId);
    modifiedAttributes.current.delete(chunkId);
  }, []);

  const getPersistData = useCallback(
    () => ({
      initialState: initialState.current,
      state,
      chunkAttributes: chunkAttributes.current,
      modifiedAttributes: modifiedAttributes.current,
      lastModifiedAttributeOwners: lastModifiedAttributeOwners.current,
    }),
    [state]
  );

  return {
    state,
    updateState,
    getInitialState,
    getStateDiff,
    getPersistData,
    getModifiedAttributes,
    getLastModifiedAttributes,
    registerInitialState,
    clearStateByChunkId,
  };
};

export default useSettingsState;
